
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useToast } from '@/hooks/use-toast';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Trash } from 'lucide-react';
import { TeamCostDisplay } from './TeamCostDisplay';

interface TeamEquipmentDialogProps {
  teamId: string;
}

export const TeamEquipmentDialog = ({ teamId }: TeamEquipmentDialogProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { isAdmin } = useUserRoles();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Only fetch data if the user is an admin
  const { data: equipmentData, isLoading } = useQuery({
    queryKey: ['team-equipment-details', teamId],
    queryFn: async () => {
      // TODO: Equipment tables not yet created - temporarily disabled
      return { items: [], totalCost: 0 };
    },
    enabled: isAdmin
  });

  const handleDeleteEquipment = async (equipmentId: string) => {
    // TODO: Equipment tables not yet created - temporarily disabled
    toast({
      variant: "destructive",
      title: "Feature not available",
      description: "Equipment management tables need to be created first.",
    });
  };
  
  if (!isAdmin) return null;
  
  const totalCost = equipmentData?.totalCost || 0;
  
  return (
    <>
        <TeamCostDisplay
          cost={totalCost}
          tooltipText="Team Equipment Cost"
          displayLabel="Team Equip"
          borderClass="border-green-500"
          textClass="text-green-600"
          onClick={() => setIsDialogOpen(true)}
        />
      
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Team Equipment Details</DialogTitle>
          </DialogHeader>
          
          <div className="overflow-auto max-h-[60vh]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Equipment</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Unit Cost</TableHead>
                  <TableHead className="text-right">Total Cost</TableHead>
                  <TableHead>Assigned On</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      Loading equipment details...
                    </TableCell>
                  </TableRow>
                ) : equipmentData?.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No equipment assigned to this team.
                    </TableCell>
                  </TableRow>
                ) : (
                  equipmentData?.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-right">€{item.unitCost.toFixed(2)}</TableCell>
                      <TableCell className="text-right">€{item.totalCost.toFixed(2)}</TableCell>
                      <TableCell>{item.assignedAt}</TableCell>
                      <TableCell>{item.notes}</TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDeleteEquipment(item.id)}
                          disabled={deletingId === item.id}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash className="h-4 w-4" />
                          <span className="sr-only">Remove equipment</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
                
                {!isLoading && equipmentData?.items.length > 0 && (
                  <TableRow className="bg-muted/50">
                    <TableCell colSpan={3} className="text-right font-bold">Total Cost</TableCell>
                    <TableCell className="text-right font-bold">€{totalCost.toFixed(2)}</TableCell>
                    <TableCell colSpan={3}></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
